import { Box, Button, Checkbox, Flex, Group, Modal, Popover, SegmentedControl, Select, Stack, Table as MantineTable, Text, TextInput } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { BroomIcon, CheckIcon, ColumnsIcon, EyeIcon, LockIcon, PackageIcon, MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react"
import { forwardRef, ForwardRefRenderFunction, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import useSwr from "swr"
import { OrderApi } from "../../apis"
import { CustomsTable, ITableRef } from "../../components/custom-table"
import { useSalesActions } from "../../components/order/order-actions"
import { usePermission } from "../../components/permission"
import { PurchaseBulkRejectForm } from "../../components/purchase-sales/bulk-reject-form"
import { PurchaseSalesConfirmForm, PURCHASE_CONFIRM_MODAL_SIZE } from "../../components/purchase-sales/confirm-form"
import { purchaseColumns } from "../../components/purchase-sales/list-columns"
import { INITIAL_PURCHASE_FILTERS, PurchaseFilters, searchToFilters } from "../../components/purchase-sales/list-filters"
import { ListHead, nextSort, RowActionIcons } from "../../components/purchase-sales/list-table"
import { StackForm } from "../../components/purchase-sales/stack-form"
import { PurchaseSalesSOForm } from "../../components/purchase-sales/so-form"
import { SupplierPurchaseKanban } from "../../components/purchase-sales/supplier-purchase-kanban"
import { Sort } from "../../components/table"
import { BulkActionBar, RowCheckbox } from "../../components/ui"
import { IOrderPlus } from "../../models/Order"
import { IAuth } from "../../store/Auth"
import { IGeneral } from "../../store/General"
import classes from "../../components/purchase-sales/list-table.module.css"
import { DisabledReason, useHelpContext, EmptyStateGuide } from "@dehub/theme"
import { LEGACY_DELIVERY_ORDER_STATUSES } from "../../utils/order-delivery-status"

export type SupplierPurchaseListRef = {
  getFilters: () => PurchaseFilters
  getOrder: () => [string, "ASC" | "DESC"]
}

type Props = {
  checked: IOrderPlus[]
  setChecked: (rows: IOrderPlus[]) => void
}

/** Rows the bulk bar can act on: «Ирсэн» POs addressed to this business (approve, decline and backorder all need that). */
const isSelectable = (row: IOrderPlus, myBusinessId?: string) => row.orderStatus === "REVIEWED" && row.receiverBusiness?.id === myBusinessId

const Component: ForwardRefRenderFunction<SupplierPurchaseListRef, Props> = ({ checked, setChecked }, ref) => {
  const { t } = useTranslation(["order", "supplierSales", "common"])
  const navigate = useNavigate()
  const tableRef = useRef<ITableRef>(null)
  const user = useSelector((s: { auth: IAuth }) => s.auth.user)
  const orderStatus = useSelector((s: { general: IGeneral }) => s.general.orderStatus)
  const myBusinessId = user?.currentBusiness?.id

  const { stateOf, permissionState } = useSalesActions()
  const [bulk, setBulk] = useState<"approve" | "decline" | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)
  const closeBulk = () => !bulkBusy && setBulk(null)
  const ORD_PO_LIST = usePermission("ORD_PO_LIST")
  const ORD_LIST = usePermission("ORD_LIST")

  // The form edits a draft; «Хайх» applies it, «Цэвэрлэх» resets both.
  const [searchText, setSearchText] = useState("")
  const [draft, setDraft] = useState<Pick<PurchaseFilters, "orderStatus" | "deliveryDate">>({ orderStatus: null, deliveryDate: [null, null] })
  const [filters, setFilters] = useState<PurchaseFilters>(INITIAL_PURCHASE_FILTERS)
  const [sorted, setSorted] = useState<[string, Sort]>(["createdAt", Sort.DESC])
  const [viewMode, setViewMode] = useState<"list" | "kanban">("list")
  const [extraColumns, setExtraColumns] = useState<string[]>([])
  const [currentRows, setCurrentRows] = useState<IOrderPlus[]>([])
  const [action, setAction] = useState<string[]>([])

  const apply = () => setFilters({ ...INITIAL_PURCHASE_FILTERS, ...draft, ...searchToFilters(searchText) })
  const clear = () => {
    setSearchText("")
    setDraft({ orderStatus: null, deliveryDate: [null, null] })
    setFilters(INITIAL_PURCHASE_FILTERS)
  }
  const reload = useCallback(() => tableRef.current?.reload(), [])

  useImperativeHandle(ref, () => ({ getFilters: () => filters, getOrder: () => sorted }), [filters, sorted])

  const tt = useCallback((key: string) => t(`order:purchaseSales.${key}`), [t])
  const allColumns = useMemo(() => purchaseColumns(tt), [tt])
  const columns = allColumns.filter((c) => !c.optional || extraColumns.includes(c.id))

  const { data: reviewedCountRes } = useSwr<{ count?: number }>(
    ORD_PO_LIST.hasAccess || ORD_LIST.hasAccess ? "swr.order.count.customer" : null,
    () => OrderApi.count({ type: "PURCHASE", orderStatus: "REVIEWED" }),
    { revalidateOnFocus: false },
  )

  // The BE rejects the three pre-review statuses for a PO list (order/list.ts), so they are not offered.
  const statusOptions = (orderStatus || [])
    .filter((o: { code: string }) => !["DRAFT", "REGISTERED", "REVIEW_REJECTED"].includes(o.code))
    // A PO list shows the order status only; delivery stages are not one (an older backend still lists them).
    .filter((o: { code: string }) => !LEGACY_DELIVERY_ORDER_STATUSES.includes(o.code))
    .map((o: { code: string }) => ({ label: t(`order:${o.code === "REVIEWED" ? "orderStatusReceived" : "orderStatus"}.${o.code}`, { defaultValue: o.code }), value: o.code }))

  const selectableRows = currentRows.filter((r) => isSelectable(r, myBusinessId))
  const checkedSelectable = checked.filter((r) => isSelectable(r, myBusinessId))
  const allSelected = selectableRows.length > 0 && checkedSelectable.length === selectableRows.length

  // @dehub/help: the PO list is the RECEIVER side (D16). «Таны шийдвэр хүлээж буй N» feeds the drawer «Одоо» card,
  // {{count}} the bulk-approve toggletip; the first «Ирсэн» row carries the spotlight target.
  const { set: setHelpContext } = useHelpContext()
  const pendingCount = reviewedCountRes?.count ?? selectableRows.length
  const selectedCount = checkedSelectable.length
  useEffect(() => { setHelpContext({ side: "RECEIVER", vars: { pendingCount, count: selectedCount } }) }, [pendingCount, selectedCount, setHelpContext])
  const spotRowId = selectableRows[0]?.id

  const rowActions = (record: IOrderPlus) => {
    const detail = stateOf("detail", record)
    const approve = stateOf("approve", record)
    const decline = stateOf("decline", record)
    const backorder = stateOf("backorder", record)
    return [
      { key: "detail", label: t("order:purchaseSales.detail"), icon: <EyeIcon size={18} />, reason: detail.reason, helpId: record.id === spotRowId ? "row-info" : undefined, onClick: () => navigate(`/supplier-purchase/detail/${record.id}`) },
      ...(approve.applies ? [{ key: "approve", label: t("supplierSales:purchase.approve"), icon: <CheckIcon size={18} />, reason: approve.reason, onClick: () => setAction(["confirm", record as never, "confirm"]) }] : []),
      ...(decline.applies ? [{ key: "decline", label: t("order:purchaseSales.reject"), icon: <XIcon size={18} />, reason: decline.reason, onClick: () => setAction(["reject", record as never, "reject"]) }] : []),
      ...(backorder.applies ? [{ key: "backorder", label: t("supplierSales:sales.backorder"), icon: <PackageIcon size={18} />, reason: backorder.reason, onClick: () => setAction(["backorder", [record] as never]) }] : []),
    ]
  }

  const tableFilters = filters as unknown as Record<string, string | number | null | (string | number | Date | null)[]>

  return (
    <>
      <Flex direction="column" gap="md">
        <Group justify="space-between" align="flex-end" gap="md" wrap="wrap" className={classes.filterBar}>
          <Group gap="sm" align="flex-end" wrap="wrap">
            <TextInput
              label={t("supplierSales:purchase.searchLabel")}
              placeholder={t("supplierSales:purchase.searchPlaceholder")}
              leftSection={<MagnifyingGlassIcon size={16} />}
              value={searchText}
              onChange={(e) => setSearchText(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && apply()}
              w={260}
            />
            <Select
              label={t("order:purchaseSales.poStatus")}
              placeholder={t("supplierSales:purchase.all")}
              data={statusOptions}
              value={draft.orderStatus}
              onChange={(v) => setDraft((d) => ({ ...d, orderStatus: v }))}
              clearable
              w={200}
            />
            <DatePickerInput
              type="range"
              label={t("order:purchaseSales.deliveryDate")}
              placeholder={t("common:actions.selectDate")}
              value={draft.deliveryDate as never}
              onChange={(v) => setDraft((d) => ({ ...d, deliveryDate: v as never }))}
              clearable
              w={240}
            />
            <Button variant="default" leftSection={<BroomIcon size={16} />} onClick={clear}>{t("common:actions.clear")}</Button>
            <Button color="dark" leftSection={<MagnifyingGlassIcon size={16} />} onClick={apply}>{t("common:actions.search")}</Button>
          </Group>
          <Group gap="sm" align="flex-end" wrap="nowrap">
            <SegmentedControl
              value={viewMode}
              onChange={(v) => setViewMode(v as "list" | "kanban")}
              data={[
                { value: "list", label: t("supplierSales:purchase.viewList") },
                { value: "kanban", label: `${t("supplierSales:purchase.viewBoard")} ${reviewedCountRes?.count ?? 0}` },
              ]}
            />
            {viewMode === "list" && (
              <Popover position="bottom-end" withArrow shadow="md" width={280}>
                <Popover.Target>
                  <Button variant="default" leftSection={<ColumnsIcon size={16} />}>{t("supplierSales:sales.list.columnsMenu")}</Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <Stack gap="xs">
                    <Text fw={650} fz={14}>{t("supplierSales:sales.list.columnsMenuTitle")}</Text>
                    <Checkbox.Group value={extraColumns} onChange={setExtraColumns}>
                      <Stack gap="xs">
                        {allColumns.filter((c) => c.optional).map((c) => (
                          <Checkbox key={c.id} value={c.id} label={c.label} />
                        ))}
                      </Stack>
                    </Checkbox.Group>
                  </Stack>
                </Popover.Dropdown>
              </Popover>
            )}
          </Group>
        </Group>

        {viewMode === "list" ? (
          <CustomsTable
            ref={tableRef}
            name="order-purchase.list"
            sorted={sorted}
            filters={tableFilters}
            emptyTitle={t("supplierSales:purchase.noData")}
            // Content-driven empty state (spec 10 §2.10): default when the list is empty, «PENDING» when the Ирсэн filter finds nothing.
            renderEmpty={(state) => state === "empty" ? <EmptyStateGuide helpKey="order.supplier-purchase-list.empty.main" /> : filters.orderStatus === "REVIEWED" ? <EmptyStateGuide helpKey="order.supplier-purchase-list.empty.main" when="PENDING" /> : null}
            onClearFilters={clear}
            onResult={(res: { rows: IOrderPlus[] }) => setCurrentRows(res?.rows ?? [])}
            loadData={(data) => OrderApi.list(data!)}
            thead={
              <ListHead
                columns={columns}
                actionLabel={t("common:table.action")}
                sorted={sorted}
                onSort={(k) => setSorted((s) => nextSort(s, k))}
                select={{
                  label: t("supplierSales:sales.list.selectAll"),
                  checked: allSelected,
                  indeterminate: checkedSelectable.length > 0 && !allSelected,
                  disabled: selectableRows.length === 0,
                  onChange: () => setChecked(allSelected ? [] : selectableRows),
                }}
              />
            }
            tbody={(rows: IOrderPlus[]) => (
              <MantineTable.Tbody>
                {rows.map((record, index) => (
                  <MantineTable.Tr key={record.id ?? index}>
                    <MantineTable.Td>
                      <DisabledReason helpKey="order.supplier-purchase-list.reason.row-checkbox" when={!isSelectable(record, myBusinessId)} position="bottom-start">
                        <RowCheckbox
                          aria-label={t("supplierSales:sales.list.selectRow")}
                          checked={checked.some((c) => c.id === record.id)}
                          selectable={isSelectable(record, myBusinessId)}
                          onChange={() => setChecked(checked.some((c) => c.id === record.id) ? checked.filter((c) => c.id !== record.id) : [...checked, record])}
                        />
                      </DisabledReason>
                    </MantineTable.Td>
                    <MantineTable.Td><RowActionIcons items={rowActions(record)} /></MantineTable.Td>
                    {columns.map((c) => (
                      <MantineTable.Td key={c.id} ta={c.align === "right" ? "right" : undefined}>{c.render(record, index)}</MantineTable.Td>
                    ))}
                  </MantineTable.Tr>
                ))}
              </MantineTable.Tbody>
            )}
          />
        ) : (
          <SupplierPurchaseKanban
            filters={filters}
            onClick={(record) => navigate(`/supplier-purchase/detail/${record?.id}`)}
            onAction={(key, record) => setAction([key, record as never, key])}
          />
        )}

        <Box className={classes.footnote}>
          <LockIcon size={16} />
          <Text fz={14} c="gray.8">{t("supplierSales:purchase.rowActionsNote")}</Text>
        </Box>
      </Flex>

      <Modal opened={action[0] === "confirm" || action[0] === "reject"} onClose={() => setAction([])} withCloseButton={false} size={PURCHASE_CONFIRM_MODAL_SIZE} padding={0} centered>
        <PurchaseSalesConfirmForm action={action[1] as never} setActions={setAction} constant={action[2]} reload={reload} />
      </Modal>

      <Modal opened={action[0] === "backorder"} onClose={() => setAction([])} withCloseButton={false} size="80%" padding={0} radius="lg" centered>
        <StackForm action={action} onCancel={() => setAction([])} reload={reload} />
      </Modal>

      <Modal opened={bulk === "approve"} onClose={closeBulk} closeOnEscape={!bulkBusy} closeOnClickOutside={!bulkBusy} withCloseButton={false} size={PURCHASE_CONFIRM_MODAL_SIZE} padding={0} centered>
        <PurchaseSalesSOForm action={["purchase", checkedSelectable]} onCancel={() => setBulk(null)} reload={reload} setChecked={setChecked} onBusyChange={setBulkBusy} />
      </Modal>

      <Modal opened={bulk === "decline"} onClose={closeBulk} closeOnEscape={!bulkBusy} closeOnClickOutside={!bulkBusy} withCloseButton={false} size={PURCHASE_CONFIRM_MODAL_SIZE} padding={0} centered>
        <PurchaseBulkRejectForm orders={checkedSelectable} onCancel={() => setBulk(null)} reload={reload} setChecked={setChecked} onBusyChange={setBulkBusy} />
      </Modal>

      {/* The row's actions, applied to every selected PO (as in de-invoice-admin). */}
      <BulkActionBar
        selectedCount={checkedSelectable.length}
        onClear={() => setChecked([])}
        actions={[
          { key: "backorder", label: t("supplierSales:sales.backorder"), icon: <PackageIcon size={16} />, reason: permissionState("backorder").reason, onClick: () => setAction(["backorder", checkedSelectable as never]) },
          { key: "decline", label: t("order:purchaseSales.reject"), icon: <XIcon size={16} />, reason: permissionState("decline").reason, onClick: () => setBulk("decline") },
          {
            key: "approve", label: t("supplierSales:purchase.approve"), icon: <CheckIcon size={16} />, reason: permissionState("approve").reason, primary: true, onClick: () => setBulk("approve"),
            helpKey: "order.supplier-purchase-list.action.bulk-approve", reasonHelpKey: "order.supplier-purchase-list.reason.bulk-approve", reasonCode: permissionState("approve").reason ? "PERM" : null, helpId: "bulk-approve",
          },
        ]}
      />
    </>
  )
}

export const SupplierPurchaseList = forwardRef(Component)
